import { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { colors, type, spacing, radius } from "@/theme";
import { HERO_PHOTO } from "@/content/site";
import { BACKEND_URL } from "@/lib/api";
import { useWide } from "@/lib/useWide";
import SiteFooter from "@/components/SiteFooter";

const STEPS = [
  { title: "Il cliente inquadra il QR sul tavolo", text: "Si apre il tuo menu, con le tue foto e i tuoi prezzi. Non deve scaricare niente." },
  { title: "Ordina dal suo telefono", text: "Anche in sei dallo stesso tavolo, ognuno dal proprio. Il conto resta uno solo." },
  { title: "La comanda arriva in cucina", text: "Una scheda per tavolo, sullo schermo o sul tablet. Nessuno deve venire a prendere l'ordine." },
];

function CreateButton({ testID, onClick }) {
  return (
    <button data-testid={testID} onClick={onClick} style={{ ...type.sansSemi, fontSize: 17, color: colors.onAccent, backgroundColor: colors.accent, minHeight: 56, border: "none", borderRadius: radius.md, padding: `0 ${spacing.lg}px`, width: "100%", maxWidth: 420 }}>
      Crea il tuo menu — è gratis
    </button>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const wide = useWide();
  const stepsRef = useRef(null);

  useEffect(() => { document.title = "Menu digitale con QR gratis per ristoranti | Tavoline"; }, []);

  const openDemo = () => window.open("/m/demo", "_blank", "noopener");
  const goRegister = () => navigate("/registrati");

  return (
    <div style={{ minHeight: "100vh", background: colors.paper }}>
      {/* [1] Hero */}
      <div style={{ width: "100%", minHeight: "92vh", display: "flex", flexDirection: "column", justifyContent: "flex-end", backgroundImage: `linear-gradient(rgba(26,24,21,0.35), rgba(26,24,21,0.88)), url(${HERO_PHOTO})`, backgroundSize: "cover", backgroundPosition: "center" }}>
        <div style={{ width: "100%", maxWidth: 960, margin: "0 auto", padding: `${spacing.lg}px ${spacing.lg}px ${spacing.xxl}px`, display: "flex", flexDirection: "column", justifyContent: "space-between", flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ ...type.serif, fontSize: 28, color: "#fff" }}>Tavoline</div>
            <button data-testid="home-accedi-link" onClick={() => navigate("/accedi")} style={{ background: "none", border: "none", ...type.sansMedium, fontSize: 15, color: "#fff", textDecoration: "underline" }}>Accedi</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: spacing.md, maxWidth: 680, marginTop: spacing.xxl }}>
            <div style={{ ...type.sansSemi, fontSize: 12, letterSpacing: 2, color: "#fff" }}>PER I RISTORANTI ITALIANI</div>
            <h1 style={{ ...type.serif, fontSize: wide ? 60 : 40, lineHeight: wide ? "64px" : "44px", color: "#fff", margin: 0 }}>
              I tuoi clienti ordinano dal tavolo. La comanda arriva in cucina.
            </h1>
            <p style={{ ...type.sans, fontSize: 17, lineHeight: "26px", color: "#fff", marginBottom: spacing.sm }}>
              Il menu digitale è gratis per sempre. Senza scadenza, senza carta di credito, senza costi di attivazione.
            </p>
            <CreateButton testID="hero-create-menu" onClick={goRegister} />
            <button data-testid="hero-how-it-works" onClick={() => stepsRef.current?.scrollIntoView({ behavior: "smooth" })} style={{ background: "none", border: "none", ...type.sansMedium, fontSize: 15, color: "#fff", textDecoration: "underline", alignSelf: "flex-start", padding: "8px 0" }}>
              Guarda come funziona
            </button>
          </div>
        </div>
      </div>

      <div style={{ width: "100%", maxWidth: 960, margin: "0 auto", padding: `0 ${spacing.lg}px` }}>
        {/* [2] Steps */}
        <div ref={stepsRef} data-testid="section-steps" style={{ padding: `${spacing.xxl}px 0`, borderBottom: `1px solid ${colors.rule}`, display: "flex", flexDirection: "column", gap: spacing.md }}>
          <h2 style={{ ...type.serif, fontSize: 36, lineHeight: "40px", color: colors.ink, margin: 0 }}>Come funziona</h2>
          <div style={{ display: "flex", flexDirection: wide ? "row" : "column", gap: spacing.md, marginTop: spacing.sm }}>
            {STEPS.map((s, i) => (
              <div key={s.title} style={{ flex: 1, background: colors.surface, border: `1px solid ${colors.rule}`, borderRadius: radius.md, padding: spacing.lg, display: "flex", flexDirection: "column", gap: spacing.sm }}>
                <div style={{ ...type.serif, fontSize: 44, lineHeight: "48px", color: colors.ink }}>{i + 1}</div>
                <div style={{ ...type.sansBold, fontSize: 18, lineHeight: "24px", color: colors.ink }}>{s.title}</div>
                <div style={{ ...type.sans, fontSize: 16, lineHeight: "24px", color: colors.ink }}>{s.text}</div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", flexDirection: wide ? "row" : "column", alignItems: wide ? "center" : "flex-start", gap: spacing.lg, marginTop: spacing.sm }}>
            <button data-testid="open-demo-menu" onClick={openDemo} style={{ minHeight: 52, border: `1px solid ${colors.ink}`, background: "transparent", borderRadius: radius.md, padding: `0 ${spacing.lg}px`, ...type.sansSemi, fontSize: 16, color: colors.ink }}>
              Apri un menu di esempio
            </button>
            {wide ? (
              <div data-testid="demo-qr" style={{ display: "flex", alignItems: "center", gap: spacing.md }}>
                <img src={`${BACKEND_URL}/api/public/demo/qr.png?base=${encodeURIComponent(window.location.origin)}`} alt="QR del menu di esempio" style={{ width: 120, height: 120, background: "#fff", borderRadius: radius.sm }} />
                <div style={{ ...type.sans, fontSize: 15, lineHeight: "22px", color: colors.ink, maxWidth: 240 }}>Oppure inquadralo col telefono, come farebbe un tuo cliente.</div>
              </div>
            ) : null}
          </div>
        </div>

        {/* [3] Price */}
        <div data-testid="section-price" style={{ padding: `${spacing.xxl}px 0`, borderBottom: `1px solid ${colors.rule}`, display: "flex", flexDirection: "column", gap: spacing.md }}>
          <h2 style={{ ...type.serif, fontSize: 36, lineHeight: "40px", color: colors.ink, margin: 0 }}>Quanto costa</h2>
          <div style={{ ...type.serif, fontSize: 30, lineHeight: "36px", color: colors.ink }}>Il menu digitale con QR code è gratis.</div>
          <p style={{ ...type.sans, fontSize: 18, lineHeight: "28px", color: colors.ink, maxWidth: 680, margin: 0 }}>Per sempre, senza limiti di piatti e senza scadenze.</p>
          <p style={{ ...type.sans, fontSize: 18, lineHeight: "28px", color: colors.ink, maxWidth: 680, margin: 0 }}>
            L'ordinazione al tavolo e lo schermo di cucina sono inclusi nella prova. Se un giorno diventeranno a pagamento, lo saprai prima, e il tuo menu resterà comunque online e gratuito.
          </p>
        </div>

        {/* [4] Why */}
        <div data-testid="section-why" style={{ padding: `${spacing.xxl}px 0`, borderBottom: `1px solid ${colors.rule}`, display: "flex", flexDirection: "column", gap: spacing.md, maxWidth: 720 }}>
          <h2 style={{ ...type.serif, fontSize: 36, lineHeight: "40px", color: colors.ink, margin: 0 }}>Perché lo regalo</h2>
          <p style={{ ...type.sans, fontSize: 18, lineHeight: "28px", color: colors.ink, margin: 0 }}>
            Un'app come questa, in Italia, un ristoratore la paga dai mille ai cinquemila euro: se la fa sviluppare su misura, solo per sé. Ecco perché ce l'hanno quasi solo i ristoranti di sushi.
          </p>
          <p style={{ ...type.sans, fontSize: 18, lineHeight: "28px", color: colors.ink, margin: 0 }}>
            Io l'ho costruita in due settimane usando l'intelligenza artificiale, e ho deciso di regalarla. Mi chiamo Mariana Radova e costruisco software per le aziende italiane: Tavoline è il modo più onesto che ho per far vedere come lavoro.
          </p>
          <button data-testid="why-chi-sono-link" onClick={() => navigate("/chi-sono")} style={{ background: "none", border: "none", ...type.sansSemi, fontSize: 17, color: colors.ink, textDecoration: "underline", alignSelf: "flex-start", padding: "8px 0" }}>
            Chi sono e cosa faccio →
          </button>
        </div>

        {/* [5] Close */}
        <div data-testid="section-close" style={{ padding: `${spacing.xxl}px 0`, display: "flex", flexDirection: "column", alignItems: "center", gap: spacing.md }}>
          <CreateButton testID="close-create-menu" onClick={goRegister} />
          <div style={{ ...type.sans, fontSize: 14, color: colors.inkMuted, textAlign: "center" }}>Ci metti dieci minuti. Nessuna carta richiesta.</div>
        </div>

        <SiteFooter />
      </div>
    </div>
  );
}
